import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  BedDouble,
  Bath,
  Square,
  Sparkles,
  Upload,
  Video,
  X,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const MyProperties = () => {
  const { user, loading: authLoading } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    property_type: "house",
    listing_type: "sale",
    status: "active",
    price: "",
    bedrooms: "",
    bathrooms: "",
    area_sqft: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    features: "",
    images: ""
  });

  useEffect(() => {
    if (!authLoading && user?.user_id) {
      fetchProperties();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchProperties = async () => {
    if (!user?.user_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/properties`, {
        withCredentials: true
      });
      const allProperties = response.data?.properties || [];
      const userProperties = allProperties.filter(
        p => p && (p.owner_id === user?.user_id || p.agent_id === user?.user_id)
      );
      setProperties(userProperties);
    } catch (error) {
      console.error("Error fetching properties:", error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, listingType) => {
    if (price === undefined || price === null) return 'K0';
    if (listingType === "rent") return `K${price.toLocaleString()}/mo`;
    return `K${price.toLocaleString()}`;
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const MAX_SIZE = 10 * 1024 * 1024;
    const validFiles = files.filter(file => {
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    const newFiles = [];

    for (const file of validFiles) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      try {
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
        newFiles.push(response.data);
      } catch (error) {
        console.error('Upload error:', error);
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`Failed to upload ${file.name}: ${errorMsg}`);
      }
    }

    if (newFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...newFiles]);
      const allUrls = [...uploadedFiles, ...newFiles].map(f => f.url);
      const existingUrls = formData.images ? formData.images.split(",").map(u => u.trim()).filter(Boolean) : [];
      const combinedUrls = [...existingUrls, ...allUrls.filter(url => !existingUrls.includes(url))];
      setFormData(prev => ({ ...prev, images: combinedUrls.join(", ") }));
      toast.success(`${newFiles.length} file(s) uploaded!`);
    }

    setUploading(false);
    e.target.value = '';
  };

  const removeUploadedFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    const urls = newFiles.map(f => f.url);
    const existingUrls = formData.images ? formData.images.split(",").map(u => u.trim()).filter(Boolean) : [];
    const filteredUrls = existingUrls.filter(url => !uploadedFiles[index] || url !== uploadedFiles[index].url);
    setFormData(prev => ({ ...prev, images: [...new Set([...filteredUrls, ...urls])].join(", ") }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const uploadedUrls = uploadedFiles.map(f => f.url);
      const manualUrls = formData.images ? formData.images.split(",").map(i => i.trim()).filter(Boolean) : [];
      const allImages = [...new Set([...uploadedUrls, ...manualUrls])];

      const data = {
        ...formData,
        price: parseFloat(formData.price),
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        area_sqft: parseFloat(formData.area_sqft) || 0,
        features: formData.features.split(",").map(f => f.trim()).filter(Boolean),
        images: allImages
      };

      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.property_id}`, data, {
          withCredentials: true
        });
        toast.success("Property updated!");
      } else {
        await axios.post(`${API}/properties`, data, {
          withCredentials: true
        });
        toast.success("Property created!");
      }

      setDialogOpen(false);
      setEditingProperty(null);
      resetForm();
      fetchProperties();
    } catch (error) {
      toast.error("Failed to save property");
    }
  };

  const handleDelete = async (propertyId) => {
    if (!confirm("Are you sure you want to delete this property?")) return;
    
    try {
      await axios.delete(`${API}/properties/${propertyId}`, {
        withCredentials: true
      });
      toast.success("Property deleted");
      fetchProperties();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      description: property.description,
      property_type: property.property_type,
      listing_type: property.listing_type,
      status: property.status || "active",
      price: property.price.toString(),
      bedrooms: property.bedrooms.toString(),
      bathrooms: property.bathrooms.toString(),
      area_sqft: property.area_sqft.toString(),
      address: property.address,
      city: property.city,
      state: property.state,
      zip_code: property.zip_code,
      features: property.features?.join(", ") || "",
      images: property.images?.join(", ") || ""
    });
    setUploadedFiles([]);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "", description: "", property_type: "house", listing_type: "sale",
      status: "active", price: "", bedrooms: "", bathrooms: "", area_sqft: "",
      address: "", city: "", state: "", zip_code: "",
      features: "", images: ""
    });
    setUploadedFiles([]);
  };

  const generateDescription = async () => {
    if (!formData.property_type || !formData.bedrooms || !formData.city) {
      toast.error("Please fill in property type, bedrooms, and city first");
      return;
    }

    setGeneratingDesc(true);
    try {
      const response = await axios.post(`${API}/ai/generate-description`, {
        property_type: formData.property_type,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        area_sqft: parseFloat(formData.area_sqft) || 0,
        features: formData.features.split(",").map(f => f.trim()).filter(Boolean),
        location: `${formData.city}, ${formData.state}`
      }, { withCredentials: true });

      setFormData(prev => ({ ...prev, description: response.data.description }));
      toast.success("Description generated!");
    } catch (error) {
      toast.error("Failed to generate description");
    } finally {
      setGeneratingDesc(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif">My Properties</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingProperty(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-property-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">
                {editingProperty ? "Edit Property" : "Add New Property"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Modern Luxury Villa"
                  data-testid="property-form-title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Property Type *</label>
                  <Select 
                    value={formData.property_type} 
                    onValueChange={(v) => setFormData({ ...formData, property_type: v })}
                  >
                    <SelectTrigger data-testid="property-form-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Listing Type *</label>
                  <Select 
                    value={formData.listing_type} 
                    onValueChange={(v) => setFormData({ ...formData, listing_type: v })}
                  >
                    <SelectTrigger data-testid="property-form-listing">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sale">For Sale</SelectItem>
                      <SelectItem value="rent">For Rent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {editingProperty && (
                <div>
                  <label className="block text-sm font-medium mb-1">Property Status</label>
                  <Select 
                    value={formData.status || "active"} 
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active (Available)</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="rented">Rented</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Setting status to "Rented" or "Sold" will show a badge on the property listing
                  </p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Price *</label>
                  <Input
                    required
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="500000"
                    data-testid="property-form-price"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bedrooms</label>
                  <Input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    placeholder="4"
                    data-testid="property-form-beds"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bathrooms</label>
                  <Input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    placeholder="3"
                    data-testid="property-form-baths"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Area (sq ft)</label>
                <Input
                  type="number"
                  value={formData.area_sqft}
                  onChange={(e) => setFormData({ ...formData, area_sqft: e.target.value })}
                  placeholder="2500"
                  data-testid="property-form-area"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Address *</label>
                <Input
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                  data-testid="property-form-address"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <Input
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Los Angeles"
                    data-testid="property-form-city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <Input
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="CA"
                    data-testid="property-form-state"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Zip Code *</label>
                  <Input
                    required
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    placeholder="90210"
                    data-testid="property-form-zip"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">Description *</label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={generateDescription}
                    disabled={generatingDesc}
                    data-testid="generate-desc-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    {generatingDesc ? "Generating..." : "AI Generate"}
                  </Button>
                </div>
                <Textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your property..."
                  rows={4}
                  data-testid="property-form-desc"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Features (comma-separated)</label>
                <Input
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  placeholder="Pool, Gym, Parking"
                  data-testid="property-form-features"
                />
              </div>

              {/* File Upload Section */}
              <div>
                <label className="block text-sm font-medium mb-2">Upload Photos/Videos</label>
                <div className="border-2 border-dashed border-border rounded-sm p-4 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <label 
                    htmlFor="file-upload" 
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    {uploading ? (
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    ) : (
                      <Upload className="w-8 h-8 text-muted-foreground" />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {uploading ? "Uploading..." : "Click to upload images or videos"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Supports: JPG, PNG, WebP, GIF, MP4, WebM
                    </span>
                  </label>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="relative group">
                        {file.is_video ? (
                          <div className="w-full h-20 bg-muted rounded-sm flex items-center justify-center">
                            <Video className="w-6 h-6 text-muted-foreground" />
                          </div>
                        ) : (
                          <img 
                            src={file.url} 
                            alt={`Upload ${index + 1}`}
                            className="w-full h-20 object-cover rounded-sm"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeUploadedFile(index)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Or paste Image URLs (comma-separated)</label>
                <Input
                  value={formData.images}
                  onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                  placeholder="https://..."
                  data-testid="property-form-images"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1" data-testid="property-form-submit">
                  {editingProperty ? "Update Property" : "Create Property"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-sm p-4 animate-pulse">
              <div className="h-6 bg-muted rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-muted rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-16 bg-muted rounded-sm">
          <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-serif text-xl mb-2">No Properties Yet</h3>
          <p className="text-muted-foreground mb-4">Start by adding your first property listing.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {properties.map((property) => (
            <div 
              key={property.property_id}
              className="bg-card border border-border rounded-sm p-4 flex gap-4"
            >
              <img
                src={property.images?.[0] || "https://images.unsplash.com/photo-1746458258536-b9ee5db20a73"}
                alt={property.title}
                className="w-32 h-24 object-cover rounded-sm"
              />
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{property.title}</h3>
                    <p className="text-sm text-muted-foreground">{property.city}, {property.state}</p>
                    <p className="font-mono text-primary font-semibold mt-1">
                    {formatPrice(property.price, property.listing_type)}
                      {property.listing_type === "rent" ? "" : ""}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(property)}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(property.property_id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <BedDouble className="w-3 h-3" /> {property.bedrooms}
                  </span>
                  <span className="flex items-center gap-1">
                    <Bath className="w-3 h-3" /> {property.bathrooms}
                  </span>
                  <span className="flex items-center gap-1">
                    <Square className="w-3 h-3" /> {property.area_sqft?.toLocaleString()} sqft
                  </span>
                  <Badge variant={property.status === "active" ? "default" : "secondary"} className="text-xs">
                    {property.status}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyProperties;
